import { usercontainerConstants, USERCONTAINER_UNKNOWN_OUTCOME, userConstants } from "../constants";

// containers: the user's full card list (20 built-in PLATFORM entries plus any
// THEME entries), always REPLACED wholesale from a server echo - never merged
// with, or patched from, an optimistic local ordering.
// loaded: distinguishes "never fetched" from "fetched, and empty" (same reason
// as newspin.reducer.js's savedNewsListLoaded).
// themes: the caller's own custom-card allowance, { used, allowed }, both plain
// numbers. Server-owned and PER USER - there is no client-side cap any more.
// Starts undefined, NOT a default pair: any default here (say 10) would flash
// the wrong allowance at a user allowed 1, for exactly as long as the first
// /mycontainers takes. undefined therefore means one specific thing - no
// response has ever carried an allowance - and CardConfigPopup reads it as
// "fall back to the pre-allowance copy and ceiling".
const initialState = {
    loading: false,
    creating: false,
    seeded: false,
    containers: [],
    loaded: false,
    themes: undefined,
    error: undefined,
    errorCode: undefined
};

// GET /mycontainers answers { containers, seeded, themes }. POST /container and
// POST /container/delete also carry `themes` (so the allowance refreshes on the
// two operations that can move `used`); POST /containers/order deliberately
// does not, since a reorder cannot change either number.
function toContainers(body) {
    return body && Array.isArray(body.containers) ? body.containers : [];
}

// -> the body's allowance when it is well formed, otherwise `previous`
// UNTOUCHED. Returning `previous` rather than undefined is the whole point: an
// old or rolled-back backend omits `themes` entirely, and a malformed one could
// send strings or half a pair. Neither may clobber a good allowance we already
// hold with undefined, which would silently demote the UI back to the
// pre-allowance "up to 10" copy mid-session.
//
// Note `used > allowed` is NOT malformed - it is the normal state of a user
// whose allowance was lowered below the cards they already have, and must pass
// through untouched.
function toThemes(body, previous) {
    const themes = body && body.themes;
    if (themes && typeof themes === 'object'
        && Number.isFinite(themes.used) && Number.isFinite(themes.allowed)) {
        return themes;
    }
    return previous;
}

// POST /container echoes one container, not the whole list, so it is upserted
// by id. created: false (duplicate title) echoes the EXISTING container, which
// is already in the list - upserting keeps that idempotent instead of
// inserting a second copy.
function upsertContainer(containers, container) {
    if (!container) {
        return containers;
    }
    const list = containers || [];
    // PLATFORM entries have no id at all, so only compare when both sides do.
    const index = list.findIndex(c => c.id !== undefined && container.id !== undefined && c.id === container.id);
    if (index === -1) {
        return [...list, container];
    }
    // Replace in place rather than moving the row to the end: on a duplicate
    // title the echoed container is one the user is already looking at.
    const next = [...list];
    next[index] = container;
    return next;
}

export function usercontainer(state = initialState, action) {
    switch (action.type) {
        case usercontainerConstants.USERCONTAINER_LIST_REQUEST:
            return { ...state, loading: true, error: undefined, errorCode: undefined };
        case usercontainerConstants.USERCONTAINER_LIST_SUCCESS:
            return {
                ...state,
                loading: false,
                loaded: true,
                seeded: action.payload ? action.payload.seeded === true : false,
                containers: toContainers(action.payload),
                themes: toThemes(action.payload, state.themes),
                error: undefined,
                errorCode: undefined
            };
        // Like every FAILURE case below, this spreads ...state with no `themes`
        // key, which is already the required behaviour: `themes` is never on an
        // error body at any status (a 409 limit.reached included), so the last
        // allowance we held is kept rather than being reset by a failure.
        case usercontainerConstants.USERCONTAINER_LIST_FAILURE:
            return { ...state, loading: false, error: { op: action.payload.op, status: action.payload.status }, errorCode: action.payload.errorCode };

        case usercontainerConstants.USERCONTAINER_CREATE_REQUEST:
            return { ...state, creating: true, error: undefined, errorCode: undefined };
        case usercontainerConstants.USERCONTAINER_CREATE_SUCCESS:
            return {
                ...state,
                creating: false,
                containers: upsertContainer(state.containers, action.payload && action.payload.container),
                themes: toThemes(action.payload, state.themes),
                error: undefined,
                errorCode: undefined
            };
        case usercontainerConstants.USERCONTAINER_CREATE_FAILURE:
            return { ...state, creating: false, error: { op: action.payload.op, status: action.payload.status }, errorCode: action.payload.errorCode };
        case usercontainerConstants.USERCONTAINER_CREATE_UNKNOWN:
            // Not an error: the server never answered, so whether the row was
            // committed is genuinely unknown. errorCode carries the sentinel
            // so the UI can say so and re-fetch, while error stays undefined.
            return { ...state, creating: false, error: undefined, errorCode: USERCONTAINER_UNKNOWN_OUTCOME };

        case usercontainerConstants.USERCONTAINER_DELETE_REQUEST:
            return { ...state, loading: true, error: undefined, errorCode: undefined };
        case usercontainerConstants.USERCONTAINER_DELETE_SUCCESS:
            return {
                ...state,
                loading: false,
                containers: (state.containers || []).filter(c => c.id !== action.payload.id),
                themes: toThemes(action.payload, state.themes),
                error: undefined,
                errorCode: undefined
            };
        case usercontainerConstants.USERCONTAINER_DELETE_FAILURE:
            return { ...state, loading: false, error: { op: action.payload.op, status: action.payload.status }, errorCode: action.payload.errorCode };

        case usercontainerConstants.USERCONTAINER_ORDER_REQUEST:
            return { ...state, loading: true, error: undefined, errorCode: undefined };
        case usercontainerConstants.USERCONTAINER_ORDER_SUCCESS:
            // The echoed containers are authoritative: replace wholesale and
            // discard any optimistic ordering the UI applied locally.
            //
            // Deliberately writes NO `themes` key: /containers/order carries no
            // allowance (a reorder cannot change `used` or `allowed`), so
            // running it through toThemes would be pointless and adding the key
            // at all invites someone to "fix" it later with a bare
            // action.payload.themes - which WOULD clobber a good allowance with
            // undefined on every save of a new order. The ...state spread keeps
            // the last allowance we held; leave it alone.
            return {
                ...state,
                loading: false,
                containers: toContainers(action.payload),
                error: undefined,
                errorCode: undefined
            };
        case usercontainerConstants.USERCONTAINER_ORDER_FAILURE:
            return { ...state, loading: false, error: { op: action.payload.op, status: action.payload.status }, errorCode: action.payload.errorCode };

        // This list is user-scoped; keeping it across a logout would show one
        // user's card titles to the next (same reason apikey.reducer.js resets
        // on LOGOUT). Returning initialState also resets `themes` to undefined
        // for free, which is what we want: one user's allowance must never be
        // shown to the next, and undefined falls back to the neutral copy until
        // the new session's first /mycontainers answers.
        case usercontainerConstants.USERCONTAINER_CLEAR:
        case userConstants.LOGOUT:
            return initialState;
        default:
            return state;
    }
}
